import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { useUserStore } from '../store/userStore'
import { useLanguageStore } from '../store/languageStore'

interface ProgressStats {
  user: {
    id: number
    username: string
    currentLevel: number
    totalXP: number
    studyStreak: number
    totalStudyTime: number
    speakingTime?: number
    reviewTime?: number
  }
  levelProgress: {
    currentLevel: number
    xpInCurrentLevel: number
    xpForNextLevel: number
    percentageToNextLevel: number
  }
  allTimeStats: {
    totalStudyTime: number
    totalSessions: number
    totalReviews: number
    totalXP: number
    daysActive: number
  }
  recentActivity: Array<{
    date: string
    studyTimeSeconds: number
    conversationSessions: number
    flashcardsReviewed: number
    xpGained: number
    conversationTimeSeconds?: number
    flashcardStudySeconds?: number
  }>
}

interface DailyLanguageActivity {
  date: string
  studyTimeSeconds: number
  conversationTimeSeconds: number
  flashcardStudySeconds: number
  sessionCount: number
}

export function Progress() {
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)
  const { currentLanguage, availableLanguages } = useLanguageStore()

  const [stats, setStats] = useState<ProgressStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [yearlyActivity, setYearlyActivity] = useState<DailyLanguageActivity[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/profile')
      return
    }

    loadProgressStats()
  }, [user, currentLanguage])

  useEffect(() => {
    if (user && currentLanguage) {
      loadYearlyActivity()
    }
  }, [user, currentLanguage])

  const loadYearlyActivity = async () => {
    if (!user || !currentLanguage) return

    setIsLoadingActivity(true)

    try {
      // Get activity for the past year
      const endDate = new Date()
      const startDate = new Date()
      startDate.setFullYear(startDate.getFullYear() - 1)

      const params = new URLSearchParams({
        userId: user.id.toString(),
        languageCode: currentLanguage,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      })

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/progress/language-activity?${params}`
      )

      if (!response.ok) {
        throw new Error('Failed to load language activity')
      }

      const data = await response.json()
      setYearlyActivity(data.activity || [])
    } catch (err) {
      console.error('Failed to load yearly activity:', err)
    } finally {
      setIsLoadingActivity(false)
    }
  }

  const loadProgressStats = async () => {
    if (!user) return

    setIsLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        userId: user.id.toString(),
        recentDays: '30',
      })

      // Add language code if available
      if (currentLanguage) {
        params.append('languageCode', currentLanguage)
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/progress/stats?${params}`)

      if (!response.ok) {
        throw new Error('Failed to load progress stats')
      }

      const data = await response.json()
      setStats(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load progress')
    } finally {
      setIsLoading(false)
    }
  }

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)

    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  // Helper to get language name
  const getLanguageName = (code: string) => {
    const lang = availableLanguages.find(l => l.code === code)
    return lang?.name || code
  }

  // Helper to get local date string (YYYY-MM-DD) to avoid timezone issues
  const getLocalDateString = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Generate yearly tracker grid (52 weeks x 7 days)
  const generateYearlyGrid = () => {
    const today = new Date()
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)

    // Adjust to start on Sunday
    const dayOfWeek = oneYearAgo.getDay()
    oneYearAgo.setDate(oneYearAgo.getDate() - dayOfWeek)

    const grid: Array<{ date: string; seconds: number; conversation: number; flashcard: number }> = []
    const activityMap = new Map<string, DailyLanguageActivity>()

    yearlyActivity.forEach(day => {
      activityMap.set(day.date, day)
    })

    const current = new Date(oneYearAgo)
    // Generate enough days to fill the grid until today (plus padding to finish the week)
    while (current <= today || current.getDay() !== 0) {
      const dateStr = getLocalDateString(current)
      const activity = activityMap.get(dateStr)
      grid.push({
        date: dateStr,
        seconds: activity?.studyTimeSeconds || 0,
        conversation: activity?.conversationTimeSeconds || 0,
        flashcard: activity?.flashcardStudySeconds || 0
      })
      current.setDate(current.getDate() + 1)
    }

    return grid
  }

  // Get color intensity based on seconds studied
  const getActivityColor = (seconds: number): string => {
    if (seconds === 0) return 'bg-gray-100 dark:bg-gray-800'
    if (seconds < 60) return 'bg-emerald-100 dark:bg-emerald-950' // < 1 minute
    const minutes = Math.floor(seconds / 60)
    if (minutes < 5) return 'bg-emerald-200 dark:bg-emerald-900'
    if (minutes < 15) return 'bg-emerald-300 dark:bg-emerald-700'
    if (minutes < 30) return 'bg-emerald-400 dark:bg-emerald-600'
    return 'bg-emerald-500 dark:bg-emerald-500'
  }

  // Get last 14 days for daily breakdown
  const getLast14Days = () => {
    const days: Array<{ date: string; conversationSeconds: number; flashcardSeconds: number; totalSeconds: number }> = []
    const activityMap = new Map<string, DailyLanguageActivity>()

    yearlyActivity.forEach(day => {
      activityMap.set(day.date, day)
    })

    for (let i = 13; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = getLocalDateString(date)
      const activity = activityMap.get(dateStr)

      days.push({
        date: dateStr,
        conversationSeconds: activity ? activity.conversationTimeSeconds : 0,
        flashcardSeconds: activity ? activity.flashcardStudySeconds : 0,
        totalSeconds: activity ? activity.studyTimeSeconds : 0,
      })
    }

    return days
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="page-shell flex items-center justify-center">
        <div className="text-center">
          <p className="text-error-600 mb-4">{error || 'Failed to load progress'}</p>
        </div>
      </div>
    )
  }

  const last14Days = getLast14Days()
  const maxDailySeconds = Math.max(...last14Days.map(d => d.totalSeconds), 60) // At least 1 minute scale

  return (
    <div className="page-shell bg-[var(--color-bg)] min-h-screen pb-12">
      {/* Header */}
      <div className="bg-[var(--color-surface)] border-b border-[var(--color-border)] px-4 py-8 shadow-sm">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="text-[var(--color-muted)] hover:text-[var(--color-text)] mb-6 flex items-center text-sm font-medium transition-colors duration-200"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </button>
          <div className="flex items-baseline gap-4">
            <h1 className="text-3xl font-semibold text-[var(--color-text)] tracking-tight">Your Progress</h1>
            <span className="text-[var(--color-muted)] text-lg">
              {currentLanguage ? getLanguageName(currentLanguage) : 'Overview'}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Level & XP Progress */}
        <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-8 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50 rounded-full -mr-32 -mt-32 opacity-50" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-4xl font-semibold text-[var(--color-text)]">Level {stats.user.currentLevel}</h2>
                <p className="text-[var(--color-muted)] mt-2 text-lg">{stats.user.totalXP.toLocaleString()} Total XP</p>
              </div>
              <div className="w-20 h-20 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center shadow-inner">
                <span className="text-3xl font-semibold">{stats.user.currentLevel}</span>
              </div>
            </div>

            {/* XP Progress Bar */}
            <div>
              <div className="flex justify-between text-sm font-medium text-[var(--color-text-secondary)] mb-3">
                <span>Progress to Level {stats.levelProgress.currentLevel + 1}</span>
                <span>{stats.levelProgress.percentageToNextLevel}%</span>
              </div>
              <div className="w-full bg-[var(--color-border)] rounded-full h-4 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-primary-500 to-primary-400 h-4 rounded-full transition-all duration-1000 ease-out shadow-sm"
                  style={{ width: `${stats.levelProgress.percentageToNextLevel}%` }}
                />
              </div>
              <p className="text-sm text-[var(--color-muted)] mt-3 text-right">
                {stats.levelProgress.xpInCurrentLevel} / {stats.levelProgress.xpForNextLevel - stats.levelProgress.currentLevel * 300} XP needed
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Streak */}
          <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-6 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[var(--color-muted)] text-sm font-medium uppercase tracking-wide">Study Streak</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <p className="text-4xl font-semibold text-[var(--color-text)]">{stats.user.studyStreak}</p>
                  <p className="text-sm text-[var(--color-muted)]">days</p>
                </div>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-2xl">
                🔥
              </div>
            </div>
          </div>

          {/* Total Study Time */}
          <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-6 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[var(--color-muted)] text-sm font-medium uppercase tracking-wide">Total Study Time</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <p className="text-4xl font-semibold text-[var(--color-text)]">
                    {Math.floor(stats.allTimeStats.totalStudyTime / 3600)}
                  </p>
                  <p className="text-sm text-[var(--color-muted)]">hours</p>
                </div>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-2xl">
                ⏱️
              </div>
            </div>
          </div>

          {/* Days Active */}
          <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-6 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[var(--color-muted)] text-sm font-medium uppercase tracking-wide">Days Active</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <p className="text-4xl font-semibold text-[var(--color-text)]">{stats.allTimeStats.daysActive}</p>
                  <p className="text-sm text-[var(--color-muted)]">days</p>
                </div>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-2xl">
                📅
              </div>
            </div>
          </div>
        </div>

        {/* Yearly Activity Heatmap */}
        {currentLanguage && (
          <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-semibold text-[var(--color-text)]">
                Study Consistency
              </h2>
              <div className="flex items-center gap-3 text-xs font-medium text-gray-500">
                <span>Less</span>
                <div className="flex gap-1.5">
                  <div className="w-3.5 h-3.5 bg-gray-100 rounded-sm" />
                  <div className="w-3.5 h-3.5 bg-emerald-200 rounded-sm" />
                  <div className="w-3.5 h-3.5 bg-emerald-300 rounded-sm" />
                  <div className="w-3.5 h-3.5 bg-emerald-400 rounded-sm" />
                  <div className="w-3.5 h-3.5 bg-emerald-500 rounded-sm" />
                </div>
                <span>More</span>
              </div>
            </div>
            {isLoadingActivity ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="overflow-x-auto pb-4">
                <div className="min-w-[800px]">
                  <div className="flex text-xs font-medium text-gray-400 mb-2 ml-9 gap-[42px]">
                    {/* Rough approximation of month labels */}
                    <span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span>
                    <span>Jul</span><span>Aug</span><span>Sep</span><span>Oct</span><span>Nov</span><span>Dec</span>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="flex flex-col gap-1.5 text-xs font-medium text-gray-400 mr-2 h-[110px] justify-between py-1">
                      <span>Mon</span>
                      <span>Wed</span>
                      <span>Fri</span>
                    </div>
                    <div className="flex flex-col flex-wrap h-[125px] gap-1.5 content-start">
                      {generateYearlyGrid().map((day, idx) => {
                        const displayTime = day.seconds < 60
                          ? `${day.seconds}s`
                          : `${Math.floor(day.seconds / 60)}m`

                        const details = []
                        if (day.conversation > 0) details.push(`${Math.floor(day.conversation / 60)}m conv`)
                        if (day.flashcard > 0) details.push(`${Math.floor(day.flashcard / 60)}m cards`)
                        const detailStr = details.length > 0 ? ` (${details.join(', ')})` : ''

                        // Format date for tooltip
                        const dateObj = new Date(day.date + 'T00:00:00') // Force local date interpretation
                        const dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

                        return (
                          <div
                            key={idx}
                            className={`w-3.5 h-3.5 rounded-[2px] transition-colors hover:ring-2 hover:ring-offset-1 hover:ring-emerald-400 ${getActivityColor(day.seconds)}`}
                            title={`${dateLabel}: ${displayTime}${detailStr}`}
                          />
                        )
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Daily Minutes Breakdown (Custom Stacked Bar Chart) */}
        {currentLanguage && (
          <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-8">
            <h2 className="text-xl font-semibold text-[var(--color-text)] mb-8">
              Daily Study Breakdown <span className="text-[var(--color-muted)] font-normal text-base ml-2">(Last 14 Days)</span>
            </h2>

            <div className="flex h-72 items-end gap-3 sm:gap-6 relative pt-6 pl-10 pr-4">
              {/* Y-Axis Labels */}
              <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-xs font-medium text-gray-400 text-right w-8">
                <span>{Math.ceil(maxDailySeconds / 60)}m</span>
                <span>{Math.ceil(maxDailySeconds / 60 / 2)}m</span>
                <span>0m</span>
              </div>

              {/* Grid Lines */}
              <div className="absolute left-10 right-0 top-6 h-[1px] bg-gray-100" />
              <div className="absolute left-10 right-0 top-[50%] h-[1px] bg-gray-100" />
              <div className="absolute left-10 right-0 bottom-8 h-[1px] bg-gray-200" />

              {/* Bars */}
              {last14Days.map((day) => {
                const totalHeight = (day.totalSeconds / maxDailySeconds) * 100
                const convHeight = (day.conversationSeconds / day.totalSeconds) * 100
                const flashHeight = (day.flashcardSeconds / day.totalSeconds) * 100

                // Use local date string parsing
                const dateObj = new Date(day.date + 'T00:00:00')
                const dayLabel = dateObj.toLocaleDateString('en-US', { weekday: 'narrow' })
                const fullDate = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

                return (
                  <div key={day.date} className="flex-1 flex flex-col items-center group relative h-full justify-end pb-8">
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-3 hidden group-hover:block z-20 w-56 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 pointer-events-none transform -translate-x-1/2 left-1/2">
                      <div className="font-bold text-base mb-2 border-b border-gray-700 pb-1">{fullDate}</div>
                      <div className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            <span className="text-gray-300">Conversation</span>
                          </div>
                          <span className="font-mono">{Math.floor(day.conversationSeconds / 60)}m</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                            <span className="text-gray-300">Flashcards</span>
                          </div>
                          <span className="font-mono">{Math.floor(day.flashcardSeconds / 60)}m</span>
                        </div>
                        <div className="border-t border-gray-700 mt-1 pt-1 flex justify-between font-bold text-emerald-400">
                          <span>Total</span>
                          <span>{Math.floor(day.totalSeconds / 60)}m</span>
                        </div>
                      </div>
                      {/* Arrow */}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                    </div>

                    {/* Bar Stack */}
                    <div
                      className="w-full max-w-[40px] bg-gray-50 rounded-t-lg overflow-hidden flex flex-col-reverse transition-all duration-300 relative hover:opacity-90 hover:shadow-md cursor-pointer"
                      style={{ height: `${Math.max(totalHeight, 1)}%` }}
                    >
                      {day.totalSeconds > 0 ? (
                        <>
                          <div
                            className="bg-blue-500 w-full transition-all duration-500"
                            style={{ height: `${convHeight}%` }}
                          />
                          <div
                            className="bg-purple-500 w-full transition-all duration-500"
                            style={{ height: `${flashHeight}%` }}
                          />
                        </>
                      ) : (
                        <div className="w-full h-1 bg-gray-100" />
                      )}
                    </div>

                    {/* X-Axis Label */}
                    <div className="absolute bottom-0 text-xs font-medium text-gray-500 mt-2">
                      {dayLabel}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Legend */}
            <div className="mt-8 flex justify-center gap-8 text-sm">
              <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-gray-700 font-medium">Conversation</span>
              </div>
              <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-100">
                <div className="w-3 h-3 bg-purple-500 rounded-full" />
                <span className="text-gray-700 font-medium">Flashcards</span>
              </div>
            </div>
          </div>
        )}

        {/* Recent Activity List */}
        <div className="bg-[var(--color-surface)] rounded-xl shadow-md border border-[var(--color-border)] p-8">
          <h2 className="text-xl font-semibold text-[var(--color-text)] mb-6">Recent Activity Log</h2>

          {stats.recentActivity.length === 0 ? (
            <div className="text-center py-12 bg-[var(--color-surface-secondary)] rounded-xl border-2 border-dashed border-[var(--color-border)]">
              <p className="text-[var(--color-muted)] font-medium">No activity recorded yet</p>
              <p className="text-sm text-[var(--color-muted)] mt-1">Start a conversation or review flashcards to see your progress!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.recentActivity.slice(0, 10).reverse().map((activity) => (
                <div
                  key={activity.date}
                  className="flex items-center justify-between p-5 bg-[var(--color-surface-secondary)] rounded-xl hover:bg-[var(--color-surface)] hover:shadow-md hover:border-[var(--color-border)] border border-transparent transition-all duration-200 group"
                >
                  <div className="flex items-center space-x-5">
                    <div className="text-center bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-100 group-hover:border-primary-100 group-hover:bg-primary-50 transition-colors">
                      <div className="text-xs font-bold text-gray-400 uppercase tracking-wider group-hover:text-primary-400">
                        {new Date(activity.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </div>
                      <div className="text-xl font-black text-gray-900 group-hover:text-primary-600">
                        {new Date(activity.date + 'T00:00:00').getDate()}
                      </div>
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-lg">
                        {formatTime(activity.studyTimeSeconds)} <span className="text-gray-500 font-normal text-base">studied</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5 flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                          {activity.conversationSessions} conversations
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                          {activity.flashcardsReviewed} cards
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-primary-600 font-bold text-lg bg-primary-50 px-3 py-1 rounded-full">
                      +{activity.xpGained} XP
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Progress
